import React, { ReactElement, useEffect } from 'react';
import TagManager from 'react-gtm-module';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import headerImage from '../../../../assets/images/sunset-paywall/sunset-paywall-header.png';
import { SUNSET_PAYWALL_CTA_URL, SUNSET_PAYWALL_ONDEMAND_CTA_URL } from '../../../constants/external-urls.const';
import { ModalTypes } from '../../../enums/modal-types.enum';
import { UserStatuses } from '../../../enums/user-statuses.enum';
import { ModalInterface } from '../../../interfaces/modal.interface';
import {
  setIsShowStudioMigrationModal,
  setIsShowSunsetOndemandModal,
  setIsShowTopUpModal,
  setIsUpsellPlansModal
} from '../../../store/slices/modals.slice';
import { selectUser } from '../../../store/slices/user.slice';
import Button from '../../Atoms/Button/Button';
import Icon from '../../Atoms/Icon/Icon';
import ModalWrapper from '../ModalWrapper/ModalWrapper';
import { SUNSET_PAYWALL_COPY, SUNSET_PAYWALL_ONDEMAND_COPY } from './SunsetPaywallModal.const';
import styles from './SunsetPaywallModal.module.scss';

const SunsetPaywallModal = ({ isOpen }: ModalInterface): ReactElement => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const user = useSelector(selectUser, shallowEqual);
  const isOndemand = user?.status === UserStatuses.ONDEMAND;
  const copy = isOndemand ? SUNSET_PAYWALL_ONDEMAND_COPY : SUNSET_PAYWALL_COPY;

  // Ondemand users with no Sessions left are locked into Unlimited and cannot dismiss,
  // EXCEPT on their Sessions pages, where we hide the modal so they can still view the
  // session they just used. It reappears as soon as they navigate elsewhere.
  const isLocked = isOndemand && !(user?.sessions_left > 0);
  const isOnSessionsRoute = location.pathname.startsWith('/sessions');
  const effectiveIsOpen = isLocked ? isOpen && !isOnSessionsRoute : isOpen;
  const isPreventClosing = isLocked;

  useEffect(() => {
    if (effectiveIsOpen) {
      TagManager.dataLayer({ dataLayer: { event: isOndemand ? 'sunsetPaywallOndemandShown' : 'sunsetPaywallShown' } });
    }
  }, [effectiveIsOpen, isOndemand]);

  const closeModal = (): void => {
    dispatch(setIsShowTopUpModal({ isShown: false }));
    dispatch(setIsUpsellPlansModal(false));
    dispatch(setIsShowSunsetOndemandModal(false));
  };

  const onCtaClick = (): void => {
    TagManager.dataLayer({ dataLayer: { event: 'sunsetPaywallCtaClicked' } });
    const url = new URL(isOndemand ? SUNSET_PAYWALL_ONDEMAND_CTA_URL : SUNSET_PAYWALL_CTA_URL);
    if (user?.id) {
      url.searchParams.set('source_user_id', String(user.id));
    }
    window.location.assign(url.toString());
  };

  const onMigrateClick = (): void => {
    TagManager.dataLayer({ dataLayer: { event: 'sunsetPaywallMigrateClicked' } });
    closeModal();
    dispatch(setIsShowStudioMigrationModal(true));
  };

  const onSeeSessionsClick = (): void => {
    TagManager.dataLayer({ dataLayer: { event: 'sunsetPaywallSeeSessionsClicked' } });
    history.push('/sessions');
  };

  return (
    <ModalWrapper
      isOpen={effectiveIsOpen}
      onRequestClose={closeModal}
      isPreventClosing={isPreventClosing}
      modalType={ModalTypes.DEFAULT}
      className={styles.modal}
    >
      <img className={styles.headerImage} src={headerImage} alt="Musiversal" />
      <div className={styles.content}>
        <h3 className={styles.title}>{copy.title}</h3>
        {copy.intro.map(paragraph => (
          <p className={styles.subtitle} key={paragraph}>
            {paragraph}
          </p>
        ))}
        <div className={styles.benefits}>
          {copy.benefits.map(({ icon, text }) => (
            <div className={styles.benefit} key={text}>
              <Icon name={icon} />
              <span>{text}</span>
            </div>
          ))}
        </div>
        <Button className={styles.ctaButton} value={copy.ctaLabel} onClick={onCtaClick} />
        {isOndemand ? (
          <>
            {isLocked && (
              <p className={styles.migrate}>
                {SUNSET_PAYWALL_ONDEMAND_COPY.sessionsPrompt}{' '}
                <button type="button" className={styles.migrateLink} onClick={onSeeSessionsClick}>
                  {SUNSET_PAYWALL_ONDEMAND_COPY.sessionsLabel}
                </button>
              </p>
            )}
            <p className={styles.migrate}>
              {SUNSET_PAYWALL_ONDEMAND_COPY.supportPrompt}{' '}
              <a className={styles.migrateLink} href={`mailto:${SUNSET_PAYWALL_ONDEMAND_COPY.supportEmail}`}>
                {SUNSET_PAYWALL_ONDEMAND_COPY.supportEmail}
              </a>
            </p>
          </>
        ) : (
          <p className={styles.migrate}>
            {SUNSET_PAYWALL_COPY.migratePrompt}{' '}
            <button type="button" className={styles.migrateLink} onClick={onMigrateClick}>
              {SUNSET_PAYWALL_COPY.migrateLabel}
            </button>
          </p>
        )}
      </div>
    </ModalWrapper>
  );
};

export default SunsetPaywallModal;
